import {DiagramActionType} from '@/enums/diagrams';
import {uuidv4} from '@/utils/uuidUtils';
import {DiagramAction} from './diagramAction';
import {FindPeaksLabelModel} from './labelModels/findPeaksLabelModel';
import {peaksColorScheme} from '@/utils/colorSchemes';
import {MarkerSeriesPeaksPoints} from '@/interfaces/markers';
import Highcharts from 'highcharts';

/*
  Класс действия поиска максимумов
*/
export class FindPeaksAction implements DiagramAction {
  // Уникальный идентификатор действия
  id: string = uuidv4();

  // Тип действия
  type = DiagramActionType.FindPeaks;

  // Ссылка на объект графика
  protected chart: any;

  // Идентификатор серий графика к которым привязано действие
  protected bindedSeries: any[] = [];

  // Идентификатор серии маркеров максимумов
  private peaksMarkerSeries: MarkerSeriesPeaksPoints[] = [];

  // Данные действия
  actionData: any;

  // Модель текстового представления
  labelModel: FindPeaksLabelModel | null;

  // Точность представления нецелых чисел
  protected viewRoundingAccuracy = 2;

  // Список отметок для максимумов
  private peaksTooltips: any[] = [];

  // Ширина тултипа
  protected tooltipBoundingBoxWidth = 64;

  // Точки максимумов
  protected peaksPoints: {x: number; y: number}[] = [];

  // Окно для надписи Максимумы не обнаружены
  protected textBoxLabel: any = undefined;

  constructor(labelModel: FindPeaksLabelModel | null = null) {
    this.labelModel = labelModel;
  }

  /*
    Применить действие
  */
  apply(chart: any, bindedSeriesIds: string[], actionData: any) {
    this.chart = chart;
    this.bindedSeries = [];
    bindedSeriesIds.forEach((bindedSeriesId: string) => {
      this.bindedSeries.push(this.chart.get(bindedSeriesId));
    });
    this.actionData = actionData;
    this.redrawPeaks(this.actionData);
    this.redrawPeaksTooltips();
  }

  /*
    Найти максимумы
  */
  private findPeaks(peaksNumber: number, series: any): any[] {
    const points = series.points;
    const peaks = [];
    if (series.visible == false) {
      return [];
    }
    let first_max_point = null;
    for (let i = 0; i < points.length; ++i) {
      const prev = i - 1 >= 0 ? points[i - 1] : null;
      const next = i + 1 < points.length ? points[i + 1] : null;
      const cur = points[i];
      // если и справа и слева меньше, то это пик
      if ((!prev || prev.y < cur.y) && (!next || cur.y > next.y)) peaks.push(cur);
      if (prev && prev.y < cur.y && next && cur.y == next.y) {
        // если вышли снизу на полку, то запоминаем
        first_max_point = cur;
      } else {
        if (first_max_point && (!next || cur.y > next.y)) {
          // нашли конец полки - фиксируем начало
          peaks.push(first_max_point);
          first_max_point = null;
        } else {
          // если это не пик, а полка перед ростом
          if (next && cur.y < next.y) {
            first_max_point = null;
          }
        }
      }
    }
    peaks.sort((a: any, b: any) => b.y - a.y);
    return peaks.slice(0, peaksNumber).map(p => ({x: p.x, y: p.y}));
  }

  newPeaksSeriesId(id: string) {
    return `peaksMarkerSeries-${id}`;
  }

  /*
    Перерисовать максимумы
  */
  private redrawPeaks(actionData: number) {
    const extremes = this.chart.xAxis[0].getExtremes();

    const peaksSeries: MarkerSeriesPeaksPoints[] = [];
    let colorIndex = 0;
    // для всех сравниваемых графиков находим точки пересечения с курсором и запоминаем их, вместе с номерами соответствующих осей
    this.bindedSeries.forEach((series: any) => {
      const points = this.findPeaks(actionData, series);
      peaksSeries.push({
        chartSeriesId: series.options.id,
        yAxis: series.options.yAxis,
        color: peaksColorScheme[colorIndex],
        points: points.filter(p => p.x >= extremes.min && p.x <= extremes.max),
      });
      colorIndex++;
    });

    // удаляем серии маркеров, для которых нет точек пересечения в данный момент
    this.peaksMarkerSeries.forEach((series: any) => {
      const findIndex = peaksSeries.findIndex(
        (newSeries: any) => newSeries.chartSeriesId === series.chartSeriesId,
      );
      if (findIndex === -1) this.chart.get(series.id)?.remove();
      else peaksSeries[findIndex] = {...peaksSeries[findIndex], id: series.id};
    });

    this.peaksMarkerSeries = [...peaksSeries];
    this.peaksMarkerSeries.forEach((series: any) => {
      series.id = series.id !== undefined ? series.id : this.newPeaksSeriesId(uuidv4());
      this.drawPeaksSeries(series.id, series.yAxis, series.color, series.points);
    });

    if (
      !this.peaksMarkerSeries.some(series => {
        return series.points.length > 0;
      })
    )
      this.showLabel('Максимумы не обнаружены');
  }

  /*
  Рисуем серию с маркерами
*/
  protected drawPeaksSeries(
    peaksMarkerSeriesId: string,
    axisNumber: number,
    peaksColor: string,
    points: any[],
  ) {
    let peaksSeries = this.chart.get(peaksMarkerSeriesId);

    if (!peaksSeries) {
      // если серия с маркерами не найдена, то создаем ее (без перерисовки)
      peaksSeries = this.chart.addSeries(
        {
          id: peaksMarkerSeriesId,
          showInLegend: false,
          data: [],
          lineWidth: 0,
          zIndex: 9999,
          marker: {
            symbol: 'triangle',
            fillColor: peaksColor,
            lineColor: peaksColor,
            radius: 7,
            enabled: true,
            zIndex: 9999,
          },
          hover: {
            enabled: false,
            halo: {
              size: 0,
            },
            inactive: {
              enabled: false,
            },
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
          yAxis: axisNumber,
        },
        false,
      );
    }
    points.sort((a: any, b: any) => a.x - b.x);
    peaksSeries.setData(points);
  }

  /*
    Отрисовать окно с текстом на графике
  */
  protected showLabel(str: string) {
    this.clearLabel();
    this.textBoxLabel = this.chart.renderer
      .label(str, 0, 2, 'rect', 0, 0, true)
      .attr({
        fill: 'rgba(255, 230, 176, 0.8)',
        stroke: 'black',
        'stroke-width': 1,
        padding: 2,
        paddingLeft: 5,
        paddingRight: 5,
        height: 16,
        r: 2,
        zIndex: 10000,
      })
      .add();
    this.textBoxLabel.align(
      Highcharts.extend(this.textBoxLabel.getBBox(), {
        align: 'center',
        x: 0,
        verticalAlign: 'top',
        y: 24,
      }),
      null,
      'spacingBox',
    );
  }

  /*
  Убрать окно с текстом
  */
  protected clearLabel() {
    if (this.textBoxLabel != undefined) {
      this.textBoxLabel.destroy();
      this.textBoxLabel = undefined;
    }
  }

  /*
    Отменить действие
  */
  cancel() {
    this.peaksMarkerSeries.forEach((series: any) => {
      this.chart.get(series.id)?.remove();
    });

    this.peaksMarkerSeries = [];
    this.clearPeaksTooltips();
    this.clearLabel();
  }

  /*
    Действия при перерисовке
  */
  render() {
    this.peaksMarkerSeries = this.peaksMarkerSeries.filter((series: any) => {
      return this.chart.yAxis[series.yAxis] !== undefined;
    });
    this.redrawPeaksTooltips();
  }

  /*
    Действия при обновлении
  */
  update(actionData: any, bindedSeriesIds: string[]) {
    this.actionData = actionData;
    this.bindedSeries = [];
    bindedSeriesIds.forEach((bindedSeriesId: string) => {
      this.bindedSeries.push(this.chart.get(bindedSeriesId));
    });
    //тут нужно найти новые точки по x координатам
    this.redrawPeaks(this.actionData);
  }

  /*
    Получить данные действия
  */
  getActionData() {
    return this.actionData;
  }

  /*
    Очистить отметки максимумов
  */
  private clearPeaksTooltips() {
    this.peaksTooltips.forEach((tooltip: any) => tooltip.destroy());
    this.peaksTooltips = [];
  }

  /*
    Перерисовать отметки максимумов
  */
  private redrawPeaksTooltips() {
    this.clearPeaksTooltips();

    this.peaksMarkerSeries.forEach((series: any) => {
      series.points.forEach((p: any) => {
        const t = this.drawTooltip({x: p.x, y: p.y}, series.yAxis);
        this.peaksTooltips.push(t);
      });
    });
  }

  /*
    Отрисовать отметку на точке
  */
  protected drawTooltip(point: any, indexAxis: number) {
    const xAxis = this.chart.xAxis[0];
    const yAxis = this.chart.yAxis[indexAxis];

    const plotX = xAxis.toPixels(point.x);
    const plotY = yAxis.toPixels(point.y);

    //Обработка выхода тултипа за границы диаграммы
    let shiftY = -38;
    let shiftX = 0;
    const height = 28;

    const extremesY = yAxis.getExtremes();
    const extremesX = xAxis.getExtremes();

    //plotMinY т.к. max в координатах соответствует min в пикселях
    const plotMinY = yAxis.toPixels(extremesY.max);
    const plotMaxX = xAxis.toPixels(extremesX.max);

    if (plotY + shiftY < plotMinY) shiftY = 8;

    if (plotX + this.tooltipBoundingBoxWidth > plotMaxX)
      shiftX = -this.tooltipBoundingBoxWidth - 2 * shiftX;

    return this.chart.renderer
      .label(this.getTooltipStr(point.x, point.y), plotX + shiftX, plotY + shiftY)
      .css({
        'fontSize': '11px',
        'color': 'rgba(117, 16, 0, 1)',
      })
      .attr({
        fill: 'rgba(255, 176, 148, 0.9)',
        stroke: 'rgba(255, 55, 0, 1)',
        'stroke-width': 1,
        padding: 2,
        paddingLeft: 2,
        paddingRight: 2,
        height: height,
        r: 2,
        zIndex: 10000,
      })
      .add();
  }

  /*
    Получить строку для отображения в тултипе
  */
  protected getTooltipStr(x: number, y: number) {
    let formatX = x.toFixed(this.viewRoundingAccuracy);
    if (this.labelModel && this.labelModel.xValueFormatter)
      formatX = this.labelModel.xValueFormatter(x);
    const formatY = y.toFixed(this.viewRoundingAccuracy);
    return `X=${formatX}<br/>Y=${formatY}`;
  }
}
